import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { markAssembledFormSchema } from '@ekhodealer/ekho-common';
import { useAuth } from '@ekhodealer/ekho-common/auth-client';
import {
	Button,
	Checkbox,
	Group,
	SkeletonRect,
	Stack,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { formatTimestampToDate } from '@common/lib/time';

import { useGetMarkAssembled } from '../../../api/progress/useGetMarkAssembled';
import { useMarkAssembled } from '../../../api/progress/useMarkAssembled';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkAssembledDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	isConfirmed: boolean;
}

/**
 * MarkAssembledDetail form component for marking a vehicle as assembled.
 * Displays a checkbox for confirmation and a submit button.
 */
export const MarkAssembledDetail = ({ actionItem }: MarkAssembledDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);
	const { user } = useAuth();

	const markAssembledMutation = useMarkAssembled(orderId);
	const { data: existingAssemblyData, isLoading: isLoadingAssemblyData } =
		useGetMarkAssembled(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markAssembledFormSchema),
		defaultValues: {
			isConfirmed: false,
		},
	});

	// Pre-populate form with existing assembly data
	useEffect(() => {
		if (existingAssemblyData?.isComplete) {
			reset({
				isConfirmed: true,
			});
		}
	}, [existingAssemblyData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		if (!data.isConfirmed) {
			return;
		}

		if (!user?.uid) {
			toast({
				message: 'Unable to mark as assembled: user not authenticated',
				variant: 'error',
			});
			return;
		}

		try {
			await markAssembledMutation.mutateAsync({
				assembly: {
					isComplete: true,
					agent: user.uid,
				},
			});
			toast({
				message: 'Vehicle marked as assembled successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to mark vehicle as assembled';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Confirm the vehicle is fully assembled per the instructions for this specific model. If you encounter missing parts or damages, contact the OEM before proceeding.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{/* Show completion status if already complete */}
				{isLoadingAssemblyData ? (
					<SkeletonRect width='100%' height={80} />
				) : existingAssemblyData?.isComplete ? (
					<Stack itemsSpacing='8'>
						<Group itemsSpacing='8'>
							<Text size='body' color='success'>
								✓ Assembly Complete
							</Text>
						</Group>
						{existingAssemblyData.dateAssembled && (
							<Text size='bodySmall' color='secondary'>
								Completed on{' '}
								{formatTimestampToDate(new Date(existingAssemblyData.dateAssembled).getTime()) ?? '—'}
							</Text>
						)}
						{existingAssemblyData.notes && (
							<Text size='bodySmall' color='secondary'>
								Notes: {existingAssemblyData.notes}
							</Text>
						)}
					</Stack>
				) : null}

				{/* Show form if not complete */}
				{!existingAssemblyData?.isComplete && (
					<>
						<Controller
							name='isConfirmed'
							control={control}
							render={({ field: { onChange, value } }) => (
								<Stack itemsSpacing='4'>
									<Group
										itemsSpacing='8'
										itemsAlignY='center'
										itemsAlignX='start'>
										<Checkbox
											isSelected={value}
											onChange={onChange}
											isDisabled={
												isSubmitting || markAssembledMutation.isPending
											}
											ariaLabel='Confirm vehicle assembly is complete'
										/>
										<Text size='bodySmall'>Confirm</Text>
									</Group>
									{errors.isConfirmed && (
										<Text size='footnote' color='error'>
											{errors.isConfirmed.message}
										</Text>
									)}
								</Stack>
							)}
						/>

						<Button
							size='large'
							isFullWidth
							onPress={() => {
								submitHandler();
							}}
							isLoading={isSubmitting || markAssembledMutation.isPending}
							isDisabled={isLoadingAssemblyData}>
							{actionItem.config.buttonTitle || 'Mark as assembled'}
						</Button>
					</>
				)}
			</Stack>
		</Stack>
	);
};
